import { ResourceModel } from "@bryntum/schedulerpro";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import {ResourceModelTagMixin} from "./mixins/ResourceModelTagMixin.jsx";
import {ModelEntityReminderStatusMixin} from "./mixins/ModelEntityReminderStatusMixin.jsx";
import HasReminderMixin from "./mixins/HasReminderMixin.jsx";
import {getAging} from "../../other/utils/utils.jsx";
import HasAssignedToMixin from "./mixins/HasAssignedToMixin.jsx";
import { RESOURCE_LABEL_MAX_LENGTH } from "../../other/constants/OtherKeys.jsx";
import { HasIconMixin } from "./mixins/HasIconMixin.jsx";
import HasCompanyCam from "@/bryntum-lib/models/mixins/HasCompanyCam.jsx";
import { BoardUser } from "@/modern/components/user-avatar";
import { Team } from "./Team.jsx";

export class Resource extends HasReminderMixin(HasCompanyCam(ResourceModelTagMixin(ModelEntityReminderStatusMixin(HasAssignedToMixin(HasIconMixin(ResourceModel)))))) {

  declare isPhantom: boolean;
  declare id: number | string;
  declare location: string;
  declare position: string;
  declare description: string;
  declare icon: string;
  declare lastNotifiedDate: string;
  declare jobTitle: string;
  declare notes: string;
  declare order: number;
  declare archived: boolean;
  declare scheduleModifiedDate: string;
  declare creatorId: number;
  declare createdAt: Date;
  declare updaterId: number;
  declare updatedAt: Date;
  declare hideAgenda: boolean;
  declare userId: number;
  declare checklistCount: number;
  declare checklistStatus: string;
  declare eligibleBoardUsers: number[];
  declare creatorRecord: BoardUser;
  declare updaterRecord: BoardUser;
  declare isResource: boolean;
  declare _team: Team;


  static get $name() {
    return "Resource";
  }

  static get relations() {
    return {
      creatorRecord: {
        foreignKey: "creatorId",
        foreignStore: STORE_KEYS.boardUserStore,
      },
      updaterRecord: {
        foreignKey: "updaterId",
        foreignStore: STORE_KEYS.boardUserStore,
      },
    }
  }

  static get fields() {
    return [
      { name: "name", dataSource: "label" },
      { name: "location", dataSource: "location" },
      { name: "position" },
      { name: "description", dataSource: "display_name" },
      { name: "icon" },
      { name: "lastNotifiedDate", dataSource: "last_notified_date" },
      { name: "jobTitle", dataSource: "job_title" },
      { name: "notes" },
      { name: "order" },
      { name: "archived", type: "boolean", defaultValue: false },
      { name: "scheduleModifiedDate", dataSource: "schedule_modified_date" },
      { name: "creatorId", dataSource: "creator", type: "number"},
      { name: "createdAt", dataSource: "created_at", type: "date" },
      { name: "updaterId", dataSource: "updater", type: "number"},
      { name: "updatedAt", dataSource: "updated_at", type: "date" },
      { name: "aging", persist: false },
      { name: "hideAgenda", dataSource: "hide_agenda", type: "boolean", defaultValue: false },
      { name: "userId", dataSource: "user" },
      { name: "checklistCount", dataSource: "checklist_count" },
      { name: "checklistStatus", dataSource: "checklist_status" },
      { name: "eligibleBoardUsers", dataSource: "eligible_boardusers", defaultValue: [] },
    ];
  }

  static swivuGetFieldOriginalSourceName(fieldName: string) {
    const fieldsMaps: Record<string, string> = {
      eligibleUserValues: "eligible_boardusers"
    }
    return fieldsMaps[fieldName] || super.swivuGetFieldOriginalSourceName?.(fieldName);
  }

  get companyCamIntegrationStore() {
    return this.project?.resourceIntegrationsStore;
  }

  get entityVisibleName() {
    return "Resource";
  }

  get tagStoreName() {
    return STORE_KEYS.resourceTagValuesStore;
  }

  get resourceTagValues() {
    return this.project?.resourceTagValues;
  }

  get resource() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.project as any)?.resourceStore?.getById((this as any).resourceId);
  }

  get taskEvents() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.project as any)?.assignmentStore.reduce((result: any, record: any) => {
      if (record.resourceId === this.id) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result.push((this.project as any)?.eventStore.getById(record.eventId));
      }
      return result;
    }, []);
  }

  get entityType() {
    return 'resourceId' as const;
  }

  get aging() {
    return getAging(this.get("createdAt"));
  }

  valueOf() {
    return this.name;
  }

  truncateText(text: string | undefined): string | undefined {
    if (text) {
      return text.length > RESOURCE_LABEL_MAX_LENGTH  ? text.substring(0, RESOURCE_LABEL_MAX_LENGTH ) : text;
    }
    return undefined;
  }

  set name(name: string) {
    this.set('name', this.truncateText(name));
  }

  get name(): string {
    return this.truncateText(this.get('name')) || '';
  }

  get eligibleBoardUserRecords() {
    if (this.project) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (this.project.boardUsersStore as any).query((boardUserRecord: any) => this.eligibleBoardUsers.includes(boardUserRecord.id), true);
    }
    return null;
  }

  get eligibleUserValues() {
    if (this.project) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (this.project.boardUsersStore as any).query((boardUserRecord: any) => this.eligibleBoardUsers.includes(boardUserRecord.id), true).map((boardUserRecord: any) => boardUserRecord.name);
    }
    return null;
  }
}

// @ts-expect-error - exposeProperties is internal method
Resource.exposeProperties();
